import { SurveyStatus, type SurveyListItemForAdminResponse } from "../types/survey.types";
import type {
  GetSurveyListForAdminRequest,
  SurveyListFilters,
  SurveyListSorting,
  SurveyQuestion,
  SurveySortKey,
} from "../types/survey.types";

export const SURVEY_LIST_PAGE_SIZE = 20;

export const DEFAULT_SURVEY_LIST_SORTING: SurveyListSorting = {
  field: "created_at",
  direction: "desc",
  sortKey: "newest_first",
};

const isSurveyStatus = (value: string): value is SurveyStatus =>
  value === SurveyStatus.Open ||
  value === SurveyStatus.Closed ||
  value === SurveyStatus.Unpublished;

const toStartOfLocalDay = (date: Date) =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate());

const toEndOfLocalDay = (date: Date) =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59, 999);

const getDateFilterRange = (dateFilter: SurveyListFilters["date_filter"]) => {
  if (!dateFilter) return {};

  if (dateFilter.from || dateFilter.to) {
    return { from: dateFilter.from, to: dateFilter.to };
  }

  const today = new Date();
  const startOfToday = toStartOfLocalDay(today);

  switch (dateFilter.relative) {
    case "today":
      return { from: startOfToday.toISOString(), to: toEndOfLocalDay(today).toISOString() };
    case "this_week": {
      const dayOfWeek = startOfToday.getDay() || 7;
      const startOfWeek = new Date(startOfToday);
      startOfWeek.setDate(startOfWeek.getDate() - dayOfWeek + 1);
      const endOfWeek = new Date(startOfWeek);
      endOfWeek.setDate(endOfWeek.getDate() + 6);
      return { from: startOfWeek.toISOString(), to: toEndOfLocalDay(endOfWeek).toISOString() };
    }
    case "this_month": {
      const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
      const endOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);
      return { from: startOfMonth.toISOString(), to: toEndOfLocalDay(endOfMonth).toISOString() };
    }
    case "this_year": {
      const startOfYear = new Date(today.getFullYear(), 0, 1);
      const endOfYear = new Date(today.getFullYear(), 11, 31);
      return { from: startOfYear.toISOString(), to: toEndOfLocalDay(endOfYear).toISOString() };
    }
    case "past": {
      const startOfLastYear = new Date(today.getFullYear() - 1, 0, 1);
      const endOfLastYear = new Date(today.getFullYear() - 1, 11, 31);
      return {
        from: startOfLastYear.toISOString(),
        to: toEndOfLocalDay(endOfLastYear).toISOString(),
      };
    }
    default:
      return {};
  }
};

export const buildSurveyListApiFilters = (
  search: string,
  statusFilter: string,
  filters: SurveyListFilters,
): Omit<GetSurveyListForAdminRequest, "index" | "size"> => {
  const apiFilter: Omit<GetSurveyListForAdminRequest, "index" | "size"> = {};

  if (search.trim()) apiFilter.keyword = search.trim();
  if (statusFilter !== "all") {
    apiFilter.status = isSurveyStatus(statusFilter) ? statusFilter : undefined;
  }
  if (filters.status) apiFilter.status = filters.status;
  if (filters.title?.trim()) apiFilter.title = filters.title.trim();
  if (filters.description?.trim()) apiFilter.description = filters.description.trim();

  if (filters.points) {
    if (filters.points.min !== null) apiFilter.point_min = filters.points.min;
    if (filters.points.max !== null) apiFilter.point_max = filters.points.max;
  }

  const dateFilterRange = getDateFilterRange(filters.date_filter);
  if (dateFilterRange.from) apiFilter.end_time_from = dateFilterRange.from;
  if (dateFilterRange.to) apiFilter.end_time_to = dateFilterRange.to;
  if (filters.end_time) {
    if (filters.end_time.from.trim()) apiFilter.end_time_from = filters.end_time.from;
    if (filters.end_time.to.trim()) apiFilter.end_time_to = filters.end_time.to;
  }

  return apiFilter;
};

export const buildSurveyListSortKey = (sorting: SurveyListSorting): SurveySortKey => {
  if (sorting.sortKey) return sorting.sortKey;
  if (sorting.field === "created_date") return "created_at";
  if (sorting.field === "total_responses") return "responses_count";
  return sorting.field as SurveySortKey;
};

const SURVEY_STATUS_LABELS: Record<SurveyStatus, string> = {
  [SurveyStatus.Open]: "Open",
  [SurveyStatus.Unpublished]: "Unpublished",
  [SurveyStatus.Closed]: "Closed",
};

export const getSurveyStatus = (status: SurveyStatus) => SURVEY_STATUS_LABELS[status];

export const getSurveyStatusForList = (survey: SurveyListItemForAdminResponse) => {
  return survey.status ? getSurveyStatus(survey.status) : "";
};

export const getTimezoneOffsetMinutes = () => {
  return -new Date().getTimezoneOffset();
};

export const getRelativeSurveyTimeFilter = (timeFilter: string) => {
  switch (timeFilter) {
    case "Today":
      return "today";
    case "This Week":
      return "this_week";
    case "This Month":
      return "this_month";
    case "Last Year":
      return "past";
    default:
      return undefined;
  }
};

export const isSurveyFilterValueActive = (value: unknown) => {
  if (typeof value === "string") {
    return value.trim() !== "";
  }

  if (typeof value === "object" && value !== null) {
    return Object.keys(value).length > 0;
  }

  return false;
};

/**
 * Remove unsupported questions from the detail view and renumber the remaining questions.
 */
export const normalizeSurveyQuestions = (questions: SurveyQuestion[]) => {
  return questions
    .filter((question) => question?.type && (question.type as string) !== "location")
    .map((question, index) => ({
      ...question,
      position: index + 1,
    }));
};
